// frontend/src/utils/globalFetchWrapper.js
/**
 * Global API Fetch Wrapper
 * Handles:
 * - Automatic token refresh on 401
 * - Multi-tab synchronization
 * - Rate limit (429) handling (doesn't log out)
 * - Proper session expiry handling
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Prevent multiple concurrent refresh attempts
let refreshPromise = null;

/**
 * Try to get a fresh Firebase token
 */
async function getRefreshedToken() {
  try {
    const { getAuth } = await import('firebase/auth');
    const user = getAuth().currentUser;
    if (!user) return null;
    const freshToken = await user.getIdToken(true);
    return freshToken;
  } catch (err) {
    console.warn('Token refresh failed:', err?.message || err);
    return null;
  }
}

/**
 * Perform token refresh (with deduplication - only one refresh at a time)
 */
async function refreshToken() {
  if (refreshPromise) {
    return refreshPromise;  // Reuse in-flight refresh
  }

  refreshPromise = getRefreshedToken();
  const token = await refreshPromise;
  refreshPromise = null;

  if (token) {
    localStorage.setItem('hopeSaccoToken', token);
    console.log('✅ Token refreshed via globalFetchWrapper');
  }

  return token;
}

/**
 * Handle session expiry - clears auth and redirects
 */
function handleSessionExpiry() {
  // Only redirect if not already on login page
  if (window.location.pathname === '/login') {
    return;
  }

  console.log('🔒 Session expired - clearing auth and redirecting to login');

  // Clear all auth data
  localStorage.removeItem('hopeSaccoToken');
  localStorage.removeItem('hopeSaccoUser');
  localStorage.removeItem('lastActivityTime');

  // Notify all tabs via storage event (for multi-tab sync)
  try {
    localStorage.setItem('sessionExpired', Date.now().toString());
  } catch {}

  // Small delay to let other tabs react to storage event
  setTimeout(() => {
    window.location.href = '/login';
  }, 100);
}

/**
 * Main fetch wrapper - replaces direct fetch() calls
 * @param {string} endpoint - API endpoint (e.g. '/dashboard/summary')
 * @param {object} options - fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - fetch Response object
 * @throws {Error} if session expired or other fatal error
 */
export async function apiFetch(endpoint, options = {}) {
  let token = localStorage.getItem('hopeSaccoToken');

  // Set up auth header
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  try {
    // First attempt
    let response = await fetch(`${API_URL}${endpoint}`, config);

    // Handle 401 (Unauthorized) - attempt token refresh and retry
    if (response.status === 401) {
      console.log('🔄 Got 401 - attempting token refresh before giving up...');

      const freshToken = await refreshToken();

      if (freshToken) {
        // Retry with fresh token
        const retryConfig = {
          ...config,
          headers: {
            ...headers,
            'Authorization': `Bearer ${freshToken}`,
          },
        };

        console.log('🔄 Retrying with fresh token');
        response = await fetch(`${API_URL}${endpoint}`, retryConfig);

        if (response.status === 401) {
          // Still 401 after refresh - session is genuinely expired
          console.log('❌ Still 401 after refresh - session expired');
          handleSessionExpiry();
          throw new Error('Session expired');
        }

        // Success after refresh
        return response;
      }

      // Could not refresh token - session is gone
      console.log('❌ Could not refresh token - session expired');
      handleSessionExpiry();
      throw new Error('Session expired');
    }

    // Handle 429 (Too Many Requests / Rate Limited)
    // NOTE: Rate limiting is a RESOURCE issue, not an AUTH issue
    // Do NOT log the user out - just let the page handle the error
    if (response.status === 429) {
      console.warn('⏱️ Rate limited (429) - user stays logged in');
      // Return the 429 response so the page can handle it
      return response;
    }

    // Any other response (2xx, 3xx, 4xx, 5xx) - return as-is
    return response;

  } catch (error) {
    // Re-throw session expired errors
    if (error.message === 'Session expired') {
      throw error;
    }
    // Re-throw any other error (network, etc.)
    throw error;
  }
}

/**
 * Convenience methods matching standard fetch API
 */
export const globalApi = {
  /**
   * GET request
   * Usage: const res = await globalApi.get('/members/list');
   */
  get: (endpoint, options = {}) =>
    apiFetch(endpoint, { ...options, method: 'GET' }),

  /**
   * POST request
   * Usage: const res = await globalApi.post('/members', { name: 'John' });
   */
  post: (endpoint, data, options = {}) =>
    apiFetch(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * PUT request
   * Usage: const res = await globalApi.put('/members/123', { name: 'Jane' });
   */
  put: (endpoint, data, options = {}) =>
    apiFetch(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * PATCH request
   * Usage: const res = await globalApi.patch('/members/123', { status: 'active' });
   */
  patch: (endpoint, data, options = {}) =>
    apiFetch(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * DELETE request
   * Usage: const res = await globalApi.delete('/members/123');
   */
  delete: (endpoint, options = {}) =>
    apiFetch(endpoint, { ...options, method: 'DELETE' }),
};

export default globalApi;

/**
 * HOW TO USE IN PAGES:
 * 
 * BEFORE (direct fetch - BROKEN):
 * ================================
 * const token = localStorage.getItem('hopeSaccoToken');
 * const response = await fetch(`${API_URL}/members`, {
 *   headers: { Authorization: `Bearer ${token}` }
 * });
 * if (response.status === 401) {
 *   localStorage.clear();  // ❌ BREAKS ALL TABS
 *   navigate('/login');
 * }
 * 
 * AFTER (using globalApi - FIXED):
 * ==================================
 * import globalApi from '../utils/globalFetchWrapper';
 * 
 * try {
 *   const response = await globalApi.get('/members');
 *   
 *   if (!response.ok) {
 *     const data = await response.json();
 *     throw new Error(data.error || 'Request failed');
 *   }
 *   
 *   const data = await response.json();
 *   // Use data...
 *   
 * } catch (error) {
 *   if (error.message === 'Session expired') {
 *     // globalApi already redirected to login, do nothing
 *     return;
 *   }
 *   
 *   if (error.message.includes('Too many requests')) {
 *     setError('Please wait a moment before trying again');
 *     return;
 *   }
 *   
 *   setError(error.message || 'An error occurred');
 * }
 */
